type BackgroundAlertOptions = {
    sound?: boolean;
    vibrate?: boolean;
    flash?: boolean;
};

const FLASH_BANNER_ID = "twilio-bg-alert-banner";
const FLASH_STYLE_ID = "twilio-bg-alert-style";
const BODY_FLASH_CLASS = "twilio-bg-alert-flash";
const ORIGINAL_TITLE_KEY = "__twilioOriginalTitle";
const NEW_MESSAGE_TITLE = "【新消息】";
const NEW_MESSAGE_BANNER_TEXT = "您有新的客服消息";

let titleFlashTimer: ReturnType<typeof setInterval> | null = null;
let bannerHideTimer: ReturnType<typeof setTimeout> | null = null;
let audioCtx: AudioContext | null = null;

const ensureFlashStyles = () => {
    if (document.getElementById(FLASH_STYLE_ID)) {
        return;
    }
    const style = document.createElement("style");
    style.id = FLASH_STYLE_ID;
    style.textContent = `
      @keyframes twilio-bg-alert-pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.55; }
      }
      #${FLASH_BANNER_ID} {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 99999;
        padding: 12px 16px;
        text-align: center;
        font-size: 14px;
        font-weight: 600;
        color: #fff;
        background: #0b5cab;
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        animation: twilio-bg-alert-pulse 1s ease-in-out infinite;
        pointer-events: none;
      }
      body.${BODY_FLASH_CLASS} {
        outline: 3px solid #0b5cab;
        outline-offset: -3px;
      }
    `;
    document.head.appendChild(style);
};

const playBeep = async () => {
    try {
        const AudioContextClass =
            window.AudioContext ||
            (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!AudioContextClass) {
            return;
        }
        if (!audioCtx) {
            audioCtx = new AudioContextClass();
        }
        if (audioCtx.state === "suspended") {
            await audioCtx.resume();
        }
        const now = audioCtx.currentTime;
        const playTone = (freq: number, start: number, duration: number) => {
            const oscillator = audioCtx!.createOscillator();
            const gain = audioCtx!.createGain();
            oscillator.type = "sine";
            oscillator.frequency.value = freq;
            gain.gain.setValueAtTime(0.0001, now + start);
            gain.gain.exponentialRampToValueAtTime(0.2, now + start + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, now + start + duration);
            oscillator.connect(gain);
            gain.connect(audioCtx!.destination);
            oscillator.start(now + start);
            oscillator.stop(now + start + duration + 0.02);
        };
        // 两声短促提示，尽量兼容微信内置浏览器
        playTone(880, 0, 0.12);
        playTone(1175, 0.16, 0.14);
    } catch {
        // 部分 WebView 禁止自动播放，静默失败
    }
};

const vibrateDevice = () => {
    try {
        if (typeof navigator.vibrate === "function") {
            navigator.vibrate([120, 80, 120]);
        }
    } catch {
        // ignore
    }
};

const flashPage = () => {
    ensureFlashStyles();
    document.body.classList.add(BODY_FLASH_CLASS);

    let banner = document.getElementById(FLASH_BANNER_ID);
    if (!banner) {
        banner = document.createElement("div");
        banner.id = FLASH_BANNER_ID;
        document.body.appendChild(banner);
    }
    banner.textContent = NEW_MESSAGE_BANNER_TEXT;

    if (bannerHideTimer) {
        clearTimeout(bannerHideTimer);
    }
    bannerHideTimer = setTimeout(() => {
        document.body.classList.remove(BODY_FLASH_CLASS);
    }, 4000);

    const win = window as unknown as Record<string, string>;
    if (!win[ORIGINAL_TITLE_KEY]) {
        win[ORIGINAL_TITLE_KEY] = document.title;
    }
    const original = win[ORIGINAL_TITLE_KEY];
    let toggle = false;
    if (titleFlashTimer) {
        clearInterval(titleFlashTimer);
    }
    titleFlashTimer = setInterval(() => {
        toggle = !toggle;
        document.title = toggle ? NEW_MESSAGE_TITLE : original;
    }, 800);
};

/** 页面重新可见时停止标题闪烁与横幅强调 */
export const clearBackgroundMessageAlert = () => {
    if (titleFlashTimer) {
        clearInterval(titleFlashTimer);
        titleFlashTimer = null;
    }
    const win = window as unknown as Record<string, string>;
    if (win[ORIGINAL_TITLE_KEY]) {
        document.title = win[ORIGINAL_TITLE_KEY];
    }
    document.body.classList.remove(BODY_FLASH_CLASS);
};

/** 浮窗/切走时页内声光震动提醒 */
export const triggerBackgroundMessageAlert = (options: BackgroundAlertOptions = {}) => {
    const { sound = true, vibrate = true, flash = true } = options;
    if (sound) {
        playBeep().catch(() => undefined);
    }
    if (vibrate) {
        vibrateDevice();
    }
    if (flash) {
        flashPage();
    }
};
